// src/pages/RoleForm.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import { RoleDto } from '../types/dtos';
import { PermissionGroups, PermissionLabels } from '../constants/Permissions';
import { ArrowLeft, Save, Shield } from 'lucide-react';

export default function RoleForm() {
    const navigate = useNavigate();
    const { roleId } = useParams();
    const isEditMode = !!roleId;

    const [formData, setFormData] = useState({
        roleName: '',
        description: '',
        company: '', // Optional
        permissions: [] as string[],
    });

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isEditMode) {
            const fetchRole = async () => {
                setLoadingData(true);
                try {
                    const res = await apiClient.get<RoleDto>(`/admin/roles/${roleId}`);
                    const role = res.data;
                    setFormData({
                        roleName: role.roleName,
                        description: role.description,
                        company: role.company || '',
                        permissions: role.permissions || [],
                    });
                } catch (err: any) {
                    console.error('[RoleForm] Failed to load role:', err);
                    setError(err.response?.data?.message || 'Failed to load role details.');
                } finally {
                    setLoadingData(false);
                }
            };
            fetchRole();
        }
    }, [isEditMode, roleId]);

    const handlePermissionToggle = (permission: string) => {
        setFormData((prev) => {
            const isSelected = prev.permissions.includes(permission);
            if (isSelected) {
                return { ...prev, permissions: prev.permissions.filter((p) => p !== permission) };
            } else {
                return { ...prev, permissions: [...prev.permissions, permission] };
            }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Validate
        if (!formData.roleName.trim()) {
            setError('Role name is required.');
            setLoading(false);
            return;
        }

        try {
            if (isEditMode) {
                await apiClient.put(`/admin/roles/${roleId}`, formData);
            } else {
                await apiClient.post('/admin/roles', formData);
            }
            navigate('/roles');
        } catch (err: any) {
            console.error('[RoleForm] Failed to save role:', err);
            setError(err.response?.data?.message || 'Failed to save role. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loadingData) return <div className="flex justify-center items-center h-screen bg-gray-50"><LoadingSpinner /></div>;

    return (
        <div className="w-full">
            <div className="max-w-5xl mx-auto pt-6 pb-8">
                    {/* Breadcrumb */}
                    <nav className="mb-6 flex items-center text-sm text-gray-500">
                        <button
                            onClick={() => navigate('/roles')}
                            className="flex items-center text-blue-600 hover:underline"
                        >
                            <ArrowLeft size={16} className="mr-1" />
                            Back to Roles
                        </button>
                        <span className="mx-2">/</span>
                        <span className="text-gray-900 font-medium">
                            {isEditMode ? 'Edit Role' : 'Create New Role'}
                        </span>
                    </nav>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-white">
                                <div className="flex items-center">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-3">
                                        <Shield size={24} />
                                    </div>
                                    <div>
                                        <h1 className="text-xl font-bold text-gray-900">
                                            {isEditMode ? 'Update Role' : 'Create Role'}
                                        </h1>
                                        <p className="text-sm text-gray-500">Define the name and permissions for this role</p>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-lg text-sm font-bold hover:from-blue-800 hover:to-indigo-900 focus:outline-none shadow-md disabled:opacity-50 transition-all flex items-center"
                                >
                                    <Save size={18} className="mr-2" />
                                    {loading ? 'Saving...' : 'Save Role'}
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {error && (
                                    <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
                                        {error}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-wider">
                                            Role Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900 font-medium"
                                            placeholder="e.g. Content Manager"
                                            value={formData.roleName}
                                            onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-wider">
                                            Target Company (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900 font-medium"
                                            placeholder="e.g. Xtrem Life (Leave blank for Global)"
                                            value={formData.company}
                                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1 uppercase tracking-wider">
                                        Role Description
                                    </label>
                                    <textarea
                                        rows={3}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900 font-medium"
                                        placeholder="Describe what users with this role can do..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="p-4 bg-gray-50 border-b border-gray-100">
                                <h2 className="text-sm font-bold text-gray-700 uppercase tracking-widest">Assign Permissions</h2>
                            </div>
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {PermissionGroups.map((group) => (
                                    <div key={group.name} className="space-y-3">
                                        <h3 className="text-sm font-bold text-blue-900 border-b pb-1">{group.name}</h3>
                                        <div className="space-y-2">
                                            {group.permissions.map((perm) => (
                                                <label key={perm} className="flex items-start cursor-pointer group">
                                                    <div className="flex items-center h-5 mt-0.5">
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                                            checked={formData.permissions.includes(perm)}
                                                            onChange={() => handlePermissionToggle(perm)}
                                                        />
                                                    </div>
                                                    <div className="ml-3 text-sm">
                                                        <span className="font-medium text-gray-700 group-hover:text-blue-700 transition-colors">
                                                            {PermissionLabels[perm] || perm}
                                                        </span>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 bg-blue-50 border-t border-blue-100 text-xs text-blue-700 text-center font-medium uppercase tracking-wider">
                                {formData.permissions.length} total permissions selected
                            </div>
                        </div>

                        <div className="flex justify-end pt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/roles')}
                                className="mr-4 px-6 py-2 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                Discard Changes
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-8 py-2 bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-lg text-sm font-bold hover:from-blue-800 hover:to-indigo-900 focus:outline-none shadow-lg disabled:opacity-50 transition-all flex items-center"
                            >
                                <Save size={18} className="mr-2" />
                                {loading ? 'Saving Role...' : isEditMode ? 'Save Changes' : 'Create Role'}
                            </button>
                        </div>
                    </form>
            </div>
        </div>
    );
}
